/**
 * Gallery scene: composer offers (?test=maater-ui#composer-offers)
 *
 * Art until Rowan locks. A keyword hit is a text link under the draft.
 * Accepting it replaces that word with the existing slash badge inside the
 * textarea. Included context stays a pill with an X under the toolbar.
 */
import { useState } from 'react'
import { Circle, FileText, X, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { testIds } from '@/lib/test-ids'

function DocumentChip() {
  return (
    <Badge
      variant="secondary"
      className="flex min-w-0 max-w-full items-center gap-1 border-green-500/20 bg-green-500/10 py-0.5 pl-1.5 pr-1 text-[11px] text-green-700 dark:text-green-400"
    >
      <Circle className="h-2 w-2 shrink-0 fill-green-500" />
      <FileText className="h-3 w-3 shrink-0" />
      <span className="min-w-0 truncate">
        House sale debt picture and net walk-away scaffold (May 2026)
      </span>
      <span className="ml-0.5 shrink-0 rounded-sm p-0.5" aria-hidden>
        <X className="h-3 w-3" />
      </span>
    </Badge>
  )
}

function SlashBadge({ onRemove }: { onRemove?: () => void }) {
  return (
    <span className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-violet-500/20 bg-violet-500/10 px-1.5 py-0.5 align-middle text-xs font-medium text-violet-600 dark:text-violet-400">
      <Zap className="h-3 w-3 shrink-0" />
      <span>Pilot Feedback</span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-sm p-0.5 hover:bg-violet-500/20"
          aria-label="Remove Pilot Feedback"
          data-testid={testIds.maaterUiGallery.composerOffersRemoveSkill}
        >
          <X className="h-3 w-3" />
        </button>
      ) : (
        <X className="h-3 w-3" aria-hidden />
      )}
    </span>
  )
}

function Toolbar() {
  return (
    <div className="mt-2 flex items-center gap-2 border-t border-border/50 px-3 py-2">
      <span className="rounded-md border border-border px-2 py-1 text-xs">Agent</span>
      <span className="rounded-md border border-border px-2 py-1 text-xs">Grok 4.7</span>
      <span className="ml-auto flex h-7 w-7 items-center justify-center rounded-md bg-primary text-xs text-primary-foreground">
        ↑
      </span>
    </div>
  )
}

export function ComposerOffersScene() {
  const [accepted, setAccepted] = useState(false)
  const [preview, setPreview] = useState(false)

  return (
    <div className="space-y-8" data-testid={testIds.maaterUiGallery.composerOffers}>
      <p className="max-w-xl text-sm text-muted-foreground">
        The slash badge already lives in the draft. A keyword hit should not
        grow a second pill in the toolbar. Offer it as a text link. Accepting
        replaces the word with that badge. The document chip stays the only
        context pill, and it keeps its X.
      </p>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Current
        </h3>
        <div className="max-w-xl overflow-hidden rounded-lg border border-border bg-background">
          <p className="px-3 pt-3 text-sm text-foreground">
            with today&apos;s assessment and actions taken. Good observations and feedback
            - its good to remember
          </p>
          <div className="mt-2 flex items-center gap-2 border-t border-border/50 px-3 py-2">
            <span className="rounded-md border border-border px-2 py-1 text-xs">Agent</span>
            <span className="inline-flex items-center gap-1 rounded-md border border-violet-500/20 bg-violet-500/10 px-2 py-0.5 text-xs font-medium text-violet-600 dark:text-violet-400">
              <Zap className="h-3 w-3" />
              Pilot Feedback
            </span>
            <span className="rounded-md border border-border px-2 py-1 text-xs">Grok 4.7</span>
          </div>
          <div className="px-3 pb-2">
            <DocumentChip />
          </div>
        </div>
        <p className="max-w-xl text-xs text-muted-foreground">
          The suggestion is a toolbar pill with no X. It sits next to the same
          shape as the house-sale chip, so it reads as attached context.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Proposed
        </h3>
        <div className="max-w-xl overflow-hidden rounded-lg border border-border bg-background">
          <p className="px-3 pt-3 text-sm leading-7 text-foreground">
            with today&apos;s assessment and actions taken. Good observations and{' '}
            {accepted ? (
              <SlashBadge onRemove={() => setAccepted(false)} />
            ) : (
              <span
                className={
                  preview
                    ? 'rounded-sm bg-violet-500/20 px-0.5 text-violet-700 ring-1 ring-violet-500/40 motion-safe:animate-pulse dark:text-violet-300'
                    : undefined
                }
              >
                feedback
              </span>
            )}{' '}
            - its good to remember
          </p>
          {!accepted ? (
            <div className="px-3 pt-1">
              <button
                type="button"
                onClick={() => setAccepted(true)}
                onMouseEnter={() => setPreview(true)}
                onMouseLeave={() => setPreview(false)}
                onFocus={() => setPreview(true)}
                onBlur={() => setPreview(false)}
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                data-testid={testIds.maaterUiGallery.composerOffersUseSkill}
              >
                Use Pilot Feedback?
              </button>
            </div>
          ) : null}
          <Toolbar />
          <div className="px-3 pb-2">
            <DocumentChip />
          </div>
        </div>
        <p className="max-w-xl text-xs text-muted-foreground">
          The link stays under the draft. Hovering it pulses the word that
          click will replace with the slash badge already used by
          /pilot-feedback. The X restores the word. Sending without the click
          does not run the skill.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Blur
        </h3>
        <div className="grid max-w-xl gap-3 sm:grid-cols-2">
          <div className="overflow-hidden rounded-lg border border-border bg-background">
            <div className="relative min-h-[52px] px-3 py-3 text-sm">
              <span className="pointer-events-none absolute left-3 top-3 text-[15px] text-muted-foreground">
                Type @ to mention, or drag files here...
              </span>
              <SlashBadge />
            </div>
            <p className="px-3 pb-2 text-[11px] text-muted-foreground">
              Bug: blur treats a skill-only draft as empty, so the placeholder covers the badge.
            </p>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-background">
            <div className="min-h-[52px] px-3 py-3 text-sm">
              <SlashBadge />
            </div>
            <p className="px-3 pb-2 text-[11px] text-muted-foreground">
              A skill badge counts as content. Blur hides the placeholder.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
